// Auth state + authenticated fetch for Next webapp.
//
// State shape (matches Workbench's `auth` Redux/Zustand slice — see ops
// components/auth/implementation.md §4 AC#3):
//
//   {
//     email,         // from JWT `email` claim, REQUIRED for the auth gate
//     accessToken,   // Bitol-AS-signed JWT, Bearer'd onto API calls
//     refreshToken,  // exchanged at /oauth2/token to mint new access tokens
//     tokenExpiry,   // ABSOLUTE ms (Date.now() + expiresIn * 1000). NOT seconds.
//                    // useTokenRefresh keys on this; storing seconds silently
//                    // no-ops the proactive refresh (v1.2.5 / Workbench #99).
//     isAdmin,       // realm_access.roles includes 'admin'
//     firstName,     // hydrated by App.jsx from GET /v2/users/profile after
//     lastName,      // sign-in (NOT in the JWT — see ops doc §6 v1.2.4)
//     authMode,      // 'oauth' — single mode post-cutover
//   }
//
// Persistence: sessionStorage holds the full object as JSON. localStorage is
// used only for `refreshToken` so a closed-tab user can be silently re-signed
// without re-prompting. accessToken is never written to localStorage.
//
// The auth gate predicate is `auth?.email && auth?.accessToken` — mirroring
// Workbench. The PR #95 lesson: tokens alone are not enough; if `email` is
// missing the user silently bounces back to the landing page on every
// protected route. Audit any new consumer of auth state against this shape.

import { decodeJwtPayload } from '../utils/jwt.js'

const SESSION_KEY = 'next-auth'
const REFRESH_LS_KEY = 'next-refresh-token'
const EMAIL_LS_KEY = 'next-auth-email'
const EVENT = 'next-auth-changed'

const EMPTY = Object.freeze({
  email: '',
  accessToken: '',
  refreshToken: '',
  tokenExpiry: null,
  isAdmin: false,
  firstName: '',
  lastName: '',
  authMode: 'oauth',
})

function readSession() {
  if (typeof window === 'undefined') return { ...EMPTY }
  const raw = window.sessionStorage.getItem(SESSION_KEY)
  if (!raw) return { ...EMPTY }
  try { return { ...EMPTY, ...JSON.parse(raw) } } catch { return { ...EMPTY } }
}

function writeSession(auth) {
  if (typeof window === 'undefined') return
  window.sessionStorage.setItem(SESSION_KEY, JSON.stringify(auth))
  if (auth.refreshToken) {
    try {
      window.localStorage.setItem(REFRESH_LS_KEY, auth.refreshToken)
      window.localStorage.setItem(EMAIL_LS_KEY, auth.email || '')
    } catch { /* tests may stub localStorage; production browsers always have it */ }
  }
  window.dispatchEvent(new Event(EVENT))
}

export function getAuth() {
  return readSession()
}

// Returns the previous-session refresh token if any (set on prior sign-in,
// survives a tab close). Used by App.jsx at boot to attempt silent re-auth.
export function getPersistedRefreshToken() {
  if (typeof window === 'undefined') return null
  try { return window.localStorage.getItem(REFRESH_LS_KEY) } catch { return null }
}

export function getPersistedEmail() {
  if (typeof window === 'undefined') return null
  try { return window.localStorage.getItem(EMAIL_LS_KEY) } catch { return null }
}

export function setAuth(patch) {
  const current = readSession()
  const next = { ...current, ...patch }
  writeSession(next)
  return next
}

export function clearAuth() {
  if (typeof window === 'undefined') return
  window.sessionStorage.removeItem(SESSION_KEY)
  try {
    window.localStorage.removeItem(REFRESH_LS_KEY)
    window.localStorage.removeItem(EMAIL_LS_KEY)
  } catch { /* tests may stub localStorage */ }
  window.dispatchEvent(new Event(EVENT))
}

// Composed sign-out — real logout (#244).
//
// Local-only clears used to leave the user silently re-authed: the Bitol AS
// session cookie (`bitol_session` on api.jgp.ai) survived, so the next hit on
// /signin → /oauth2/authorize re-issued a code without prompting. The full
// flow is:
//   1. Best-effort POST /oauth2/revoke (RFC 7009) for the refresh token so
//      the AS drops the persisted grant. keepalive lets it complete after
//      the page navigation.
//   2. Wipe local session + persisted refresh token.
//   3. Full-page navigate to <issuer>/oauth2/logout?redirect=<home>, which
//      clears bitol_session and bounces back to next.actianlabs.com.
//
// Order matters: clearAuth() must happen AFTER kicking the revoke request so
// we still have the refresh token to send; the redirect itself must be a
// full-page assign (not React navigation) so the AS host gets the request
// and can drop its cookie.
const OAUTH_CLIENT_ID = 'next-webapp'
const DEFAULT_ISSUER = 'https://api.jgp.ai'
export function signOut({ issuer = DEFAULT_ISSUER, clientId = OAUTH_CLIENT_ID } = {}) {
  if (typeof window === 'undefined') return
  const { refreshToken } = readSession()
  if (refreshToken) {
    try {
      fetch(`${bitolApiBase()}/oauth2/revoke`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams({
          token: refreshToken,
          token_type_hint: 'refresh_token',
          client_id: clientId,
        }).toString(),
        keepalive: true,
      }).catch(() => {})
    } catch { /* best-effort — revoke must not block logout */ }
  }
  clearAuth()
  const redirect = `${window.location.origin}/`
  window.location.assign(
    `${issuer.replace(/\/$/, '')}/oauth2/logout?redirect=${encodeURIComponent(redirect)}`,
  )
}

export function onAuthChange(handler) {
  if (typeof window === 'undefined') return () => {}
  window.addEventListener(EVENT, handler)
  return () => window.removeEventListener(EVENT, handler)
}

// Auth gate predicate — single source of truth for "is this user signed in?"
// Mirrors Workbench: presence of both fields is the contract every protected
// route depends on. Token-only checks are insufficient (PR #95).
export function isAuthenticated() {
  const a = readSession()
  return !!(a.email && a.accessToken)
}

// Decode helper kept for back-compat with callers that imported it from
// lib/auth.js prior to the OAuth cutover. New code should import from
// utils/jwt.js.
export function decodeJwt(token) {
  return decodeJwtPayload(token)
}

export function isExpired(token) {
  const claims = decodeJwtPayload(token)
  if (!claims || typeof claims.exp !== 'number') return false
  return claims.exp * 1000 <= Date.now()
}

// ---- network ----

// `/bitol` is the same-origin path the api-vm nginx proxies to Bitol Spring
// Boot. The `location /bitol/` block does `proxy_pass http://127.0.0.1:8080/`
// (trailing slash), so the `/bitol` prefix is stripped: `/bitol/oauth2/token`
// → `127.0.0.1:8080/oauth2/token` (the AS token endpoint), and likewise for
// /oauth2/revoke + /v4/users/*. No separate nginx block needed for the OAuth
// surface. Same-origin from the browser's view, so no CORS preflight.
// Override with VITE_BITOL_API_URL=http://localhost:8080 for local dev (there
// is no `/bitol` vite proxy).
export function bitolApiBase() {
  return (import.meta.env?.VITE_BITOL_API_URL || '/bitol').trim()
}

function buildAuthHeaders() {
  const { accessToken } = readSession()
  return accessToken ? { Authorization: `Bearer ${accessToken}` } : {}
}

// Hook in by App.jsx — when fetchWithAuth sees a 401, it asks this callback
// to attempt one refresh + retry. Kept as a runtime registration to avoid
// importing the refresh module here (would create an import cycle).
let refreshHandler = null
export function registerRefreshHandler(handler) { refreshHandler = handler }

// POST /oauth2/token with grant_type=refresh_token. Returns the new
// { accessToken, refreshToken, expiresIn } on success. Throws on failure,
// with `.isTokenExpired = true` when the AS returns 400/401 (refresh token
// is past its TTL — no point retrying, surface the session-expired modal).
//
// Goes through `${bitolApiBase()}/oauth2/token` — same same-origin `/bitol`
// proxy story as the authorization-code exchange in OAuthCallbackPage.
const REFRESH_CLIENT_ID = 'next-webapp'
export async function refreshAccessToken({ refreshToken } = {}) {
  const token = refreshToken || readSession().refreshToken
  if (!token) {
    const err = new Error('No refresh token available')
    err.isTokenExpired = true
    throw err
  }
  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    client_id: REFRESH_CLIENT_ID,
    refresh_token: token,
  })
  const res = await fetch(`${bitolApiBase()}/oauth2/token`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: body.toString(),
  })
  if (!res.ok) {
    const text = await res.text().catch(() => '')
    const err = new Error(`Refresh failed (HTTP ${res.status}): ${text || 'no body'}`)
    if (res.status === 400 || res.status === 401) err.isTokenExpired = true
    throw err
  }
  const data = await res.json()
  if (!data.access_token) throw new Error('Refresh response had no access_token')
  return {
    accessToken: data.access_token,
    refreshToken: data.refresh_token || token,
    expiresIn: data.expires_in || null,
  }
}

export async function fetchWithAuth(url, options = {}) {
  const headers = { ...(options.headers || {}), ...buildAuthHeaders() }
  let res = await fetch(url, { ...options, headers })
  if (res.status === 401 && refreshHandler) {
    const ok = await refreshHandler().catch(() => false)
    if (ok) {
      const headers2 = { ...(options.headers || {}), ...buildAuthHeaders() }
      res = await fetch(url, { ...options, headers: headers2 })
    }
  }
  if (res.status === 401) clearAuth()
  return res
}

// POST /v4/users/register. Returns the parsed body on success. Registration
// itself is not part of OAuth — the user creates an account, verifies the
// 6-char code emailed to them via workbenchVerify(), then signs in via
// /oauth2/authorize.
export async function workbenchRegister({ email, password, firstName, lastName, company }) {
  const res = await fetch(`${bitolApiBase()}/v4/users/register`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      email,
      password,
      firstName,
      lastName,
      ...(company ? { company } : {}),
    }),
  })
  if (!res.ok) {
    const body = await res.text().catch(() => '')
    const err = new Error(
      res.status === 409 ? 'An account with this email already exists.' :
      `Registration failed (HTTP ${res.status}).`
    )
    err.status = res.status
    err.body = body
    throw err
  }
  return res.json().catch(() => ({}))
}

// POST /v4/users/verify. Posts the 6-character code the user received by
// email after workbenchRegister(). On success the account is marked
// validated and the user can sign in via /oauth2/authorize.
export async function workbenchVerify({ email, code }) {
  const res = await fetch(`${bitolApiBase()}/v4/users/verify`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ email, code }),
  })
  if (!res.ok) {
    const body = await res.text().catch(() => '')
    const err = new Error(
      res.status === 400 ? 'Invalid verification code.' :
      res.status === 404 ? 'No account found for that email.' :
      `Verification failed (HTTP ${res.status}).`
    )
    err.status = res.status
    err.body = body
    throw err
  }
  return res.json().catch(() => ({}))
}
